package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKatcherPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("katcher.md", FrontMatterElement.Builder { addScalar("title", "katcher"); addScalar("searchTitle", "Self-hosted crash tracker for Kotlin — Kotlin/Native, Ktor, HTMX"); addScalar("description", "A crash tracker that fits on one box: a native binary, an HTMX UI, embedded SQLite, and authentication delegated to the proxy already in front."); addScalar("library", "katcher"); addScalar("post", "/blog/crash-tracker-kotlin-native"); }))
}

@Page("/katcher")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun KatcherPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/katcher/screenshot.png", "The dashboard: crash groups for one application, grouped by fingerprint and rendered server-side")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every crash reporting service wanted one of three things: my data, a JVM with a gigabyte of heap, or a Kubernetes cluster. For a side project — or an internal tool at a company that would rather not ship stack traces to a third party — none of those is a good trade.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("native binary")
            }
            org.jetbrains.compose.web.dom.Text(", 30–50 MB resident, starting in milliseconds. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("scp")
            }
            org.jetbrains.compose.web.dom.Text(" it to a box and run it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Automatic grouping by fingerprint (message + stack trace), with a dashboard rendered server-side through ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("HTMX")
            }
            org.jetbrains.compose.web.dom.Text(" — no bundler, no Node in production. Dark and light theme, responsive.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Embedded ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("SQLite")
            }
            org.jetbrains.compose.web.dom.Text(" through sqlx4k. No database to operate.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("No login screen on purpose.")
            }
            org.jetbrains.compose.web.dom.Text(" It trusts ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("X-Auth-Request-User")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("X-Auth-Request-Email")
            }
            org.jetbrains.compose.web.dom.Text(" from oauth2-proxy, Traefik ForwardAuth or NGINX ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("auth_request")
            }
            org.jetbrains.compose.web.dom.Text(", and answers 401 without them. That buys Keycloak, Google or GitHub SSO without a line of OAuth code.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Symbolication for Android")
            }
            org.jetbrains.compose.web.dom.Text(": the Gradle plugin uploads the R8 mapping file after a build, and the server applies it to incoming traces.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Server:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""docker run -p 8080:8080 -v ./data:/data ghcr.io/youndie/katcher:latest
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A Helm chart ships in the repository under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("charts/katcher")
        }
        org.jetbrains.compose.web.dom.Text(" — it wires the ingress, the forward-auth middleware and, when MCP is enabled, the bypass for it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Client, in the app you want to hear about:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies {
    implementation("io.github.youndie.katcher:client:${'$'}katcher_version")
    implementation("io.ktor:ktor-client-cio:${'$'}ktor_version") // any Ktor engine
}
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""Katcher.start {
    remoteHost = "https://katcher.example.com"
    appKey = "<YOUR_APP_KEY>"
    release = "1.0.0"
    environment = "Production"
}
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-client") }) {
        org.jetbrains.compose.web.dom.Text("The client")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One KMP library, published for JVM, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxArm64")
        }
        org.jetbrains.compose.web.dom.Text(", both macOS targets, all three iOS targets and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mingwX64")
        }
        org.jetbrains.compose.web.dom.Text(" — the same set in every release, whatever machine cut it. Android has its own coordinate, plus a Gradle plugin that generates the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BuildConfig")
        }
        org.jetbrains.compose.web.dom.Text(" fields and uploads the mapping file. The client brings no HTTP engine of its own: pick the one your platform has (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-client-cio")
        }
        org.jetbrains.compose.web.dom.Text(" on JVM and Linux, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-client-darwin")
        }
        org.jetbrains.compose.web.dom.Text(" on Apple targets).")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Katcher.start {}")
        }
        org.jetbrains.compose.web.dom.Text(" is called from common code. It installs the platform crash hook — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Thread.setDefaultUncaughtExceptionHandler")
        }
        org.jetbrains.compose.web.dom.Text(" on the JVM, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("setUnhandledExceptionHook")
        }
        org.jetbrains.compose.web.dom.Text(" on Apple targets — and chains to whatever hook was there before, so an existing handler still runs and the process still terminates the way it did.")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Katcher.catch(e)")
            }
            org.jetbrains.compose.web.dom.Text(" reports a caught exception, fire-and-forget — no ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("runBlocking")
            }
            org.jetbrains.compose.web.dom.Text(", no coroutine scope at the call site.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Katcher.addBreadcrumb(...)")
            }
            org.jetbrains.compose.web.dom.Text(" keeps the last 50 user actions in memory and attaches them to any report.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A report is written to disk before it is sent, so a crash at launch — before the upload coroutine ever runs — is delivered on the next launch instead of being lost.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One detail worth stealing whatever you use: an atomic guard around the handler, so a crash ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("inside")
        }
        org.jetbrains.compose.web.dom.Text(" the reporter cannot re-enter it. Without one, a bad day becomes an infinite one.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("crashes-for-coding-agents") }) {
        org.jetbrains.compose.web.dom.Text("Crashes for coding agents")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The server can expose crashes over the Model Context Protocol: point an agent at a repository, ask it to look into a crash, and it reads the group, its events, breadcrumbs and context, then records the pull request that fixes it. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Off by default")
        }
        org.jetbrains.compose.web.dom.Text(" — without ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MCP_TOKEN")
        }
        org.jetbrains.compose.web.dom.Text(" the endpoint is not mounted at all: no route, no secret, nothing to reach.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Crash text is written by whoever holds an app key, and app keys ship inside client applications — so the server treats it as hostile. Content that reads as an instruction to an agent is held back, and reading a crash is split in two: structured, identifier-shaped metadata first, the free text only after the agent reports which stack frames it could locate in the repository. Neither check makes untrusted text safe — no server-side check can — they narrow the easy path.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The hook sees Kotlin exceptions that reach the runtime uncaught. A ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGSEGV")
            }
            org.jetbrains.compose.web.dom.Text(", an ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("NSException")
            }
            org.jetbrains.compose.web.dom.Text(" raised in Swift or Objective-C, a watchdog termination — all invisible to it, and still need the platform's own crash reports.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("iOS stack traces arrive the way Kotlin/Native prints them; there is no dSYM step. Android R8 mappings are the only symbolication the server does.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("On the JVM the upload runs on daemon threads: a crash on the last non-daemon thread can end the process before the send fires. The report waits on disk — in a container, that directory needs a persistent volume, or it will not survive the restart it is waiting through.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One process, one SQLite file. There is no clustering story, and none planned.")
        }
    }
}
